import { DEV, STAGE } from '../../../constants';
import { table as logTable } from '../../../util/log';
import { average } from '../../../util/array';

const CAN_MEASURE = STAGE || DEV;

const SESSION_KEYS = {
  PLAYBACK: 'Playback',
  TEXT_EDIT: 'Text Edit',
  FRAME_MANAGEMENT: 'Frame Extraction',
};

export const MEASUREMENT_KEYS = {
  CANVAS_DRAW_INTERVAL: 'Canvas Draw Request Interval (ms)',
  CHUNK_EXTRACTION_DURATION_FRAMES: 'Extraction Duration for frames (ms)',
  CHUNK_EXTRACTION_DURATION_TOTAL: 'Total Chunk Extraction Duration (ms)',
  CHUNK_DOWNLOAD_DURATION: 'Download chunk duration (ms)',
  DRAW_IMAGE: 'Draw Image Duration (ms)',
  FRAME_INTERVAL: 'Frame Interval (ms)',
  INLINE_CANVAS_QUEUE_INTERVAL: 'Inline Canvas Queue Interval (ms)',
  INLINE_CANVAS_RENDER: 'Inline Canvas Render Duration (ms)',
  INLINE_CANVAS_RENDER_TASK_INTERVAL: 'Inline Canvas Render Task Interval (ms)',
  TEXT_CHANGE_DURATION: 'Text Change Action Duration',
  TEXT_CHANGE_INTERVAL: 'Text Change Action Interval',
  TIME_TO_FIRST_FRAME: 'Time to decode and cache first frame (ms)',
  TIME_SPENT_BUFFERING: 'Time to playable state (ms)',
};

const DESCRIPTIONS = {
  [MEASUREMENT_KEYS.CANVAS_DRAW_INTERVAL]:
    "The interval between calls to 'renderFrame' in Snap Preview",
  [MEASUREMENT_KEYS.CHUNK_EXTRACTION_DURATION_FRAMES]:
    'Time it takes to extract frames of one whole chunk',
  [MEASUREMENT_KEYS.CHUNK_EXTRACTION_DURATION_TOTAL]:
    'Total time to extract one whole chunk (includes initialization and download times)',
  [MEASUREMENT_KEYS.CHUNK_DOWNLOAD_DURATION]:
    'The time it takes to download a chunk',
  [MEASUREMENT_KEYS.DRAW_IMAGE]:
    'The duration of calls to canvas context drawImage in all layers',
  [MEASUREMENT_KEYS.FRAME_INTERVAL]:
    'The interval between calls to update the current frame in state',
  [MEASUREMENT_KEYS.INLINE_CANVAS_QUEUE_INTERVAL]:
    'The interval between calls to queue a frame in InlineSnapPreview',
  [MEASUREMENT_KEYS.INLINE_CANVAS_RENDER]:
    "The duration of calls to 'renderFrame' in InlineSnapPreview",
  [MEASUREMENT_KEYS.INLINE_CANVAS_RENDER_TASK_INTERVAL]:
    'The interval between calls inside the queued render tasks in InlineSnapPreview',
  [MEASUREMENT_KEYS.TEXT_CHANGE_DURATION]:
    'Duration of time taken for onTextChanged action callback',
  [MEASUREMENT_KEYS.TEXT_CHANGE_INTERVAL]:
    'Interval of time between onTextChanged action callback',
  [MEASUREMENT_KEYS.TIME_TO_FIRST_FRAME]:
    'Time it takes from requesting a video to be decoded to when the first frame is available',
  [MEASUREMENT_KEYS.TIME_SPENT_BUFFERING]:
    'Interval of time the user waits for buffering',
};

const DEFAULT_IDENTIFIER = 'default';
class PerformanceReview {
  _intervalMarkersInUse;
  _measurementKeysInUse;
  _measurementMarkerNamesInUse;
  _sessionKey;

  constructor(sessionKey) {
    this._sessionKey = sessionKey;
    this._intervalMarkersInUse = new Set();
    this._measurementKeysInUse = new Set();
    this._measurementMarkerNamesInUse = new Set();
  }

  measureInterval(measurementKey) {
    if (!CAN_MEASURE) return;

    const markName = this.getMeasureName(measurementKey);
    performance.mark(markName);
    this._intervalMarkersInUse.add(measurementKey);
  }

  /**
   * @function start
   * @param  {String} measurementKey
   * @param  {type} intervalIdentifier A unique identifier that PerformanReview can use to differentiate
   * between two concurrent measurements. i.e. If I start two measurements concurrently with Key = `X`
   * Without using an intervalIdentifier you will get undefined behaviour of when you end one of the measurements, that is,
   * we won't know which start measurement the end measurement corresponds to.
   * In this case providing a unique identifier for both measurements will prevent that collision.
   */
  start(measurementKey, intervalIdentifier = DEFAULT_IDENTIFIER) {
    if (!CAN_MEASURE) return;

    const markName = this.getMarkStartName(measurementKey, intervalIdentifier);
    this._measurementMarkerNamesInUse.add(markName);
    performance.mark(markName);
  }

  /**
   * @function end
   * @param  {String} measurementKey
   * @param  {String} intervalIdentifier see {@link #start}
   */
  end(measurementKey, intervalIdentifier = DEFAULT_IDENTIFIER) {
    if (!CAN_MEASURE) return;

    const measureName = this.getMeasureName(measurementKey);
    const markEndName = this.getMarkEndName(measurementKey, intervalIdentifier);
    const markStartName = this.getMarkStartName(
      measurementKey,
      intervalIdentifier,
    );

    this._measurementKeysInUse.add(measurementKey);
    this._measurementMarkerNamesInUse.add(markEndName);
    performance.mark(markEndName);
    performance.measure(measureName, markStartName, markEndName);
  }

  /**
   * @function abort Just marks the end, but does not measure the diff between start and end
   * @param  {String} measurementKey
   * @param  {String} intervalIdentifier see {@link #start}
   */
  abort(measurementKey, intervalIdentifier = DEFAULT_IDENTIFIER) {
    if (!CAN_MEASURE) return;
    const markEndName = this.getMarkEndName(measurementKey, intervalIdentifier);
    performance.mark(markEndName);
    this._measurementMarkerNamesInUse.add(markEndName);
  }

  clear() {
    if (!CAN_MEASURE) return;

    this._measurementKeysInUse.forEach((measurementKey) => {
      performance.clearMeasures(this.getMeasureName(measurementKey));
    });

    this._measurementMarkerNamesInUse.forEach((markerName) => {
      performance.clearMarks(markerName);
    });

    this._intervalMarkersInUse.forEach((measurementKey) => {
      const markName = this.getMeasureName(measurementKey);
      performance.clearMarks(markName);
    });

    this._intervalMarkersInUse = new Set();
    this._measurementKeysInUse = new Set();
    this._measurementMarkersNamesInUse = new Set();
  }

  log() {
    if (!CAN_MEASURE) return;

    const tableData = {};

    this._measurementKeysInUse.forEach((measureKey) => {
      const markName = this.getMeasureName(measureKey);
      const measurements = performance.getEntriesByName(markName);
      const durations = measurements.map((entry) => entry.duration);

      const durationMean = average(durations);
      const durationMax = Math.max(...durations);
      const durationMin = Math.min(...durations);

      tableData[measureKey] = {
        description: DESCRIPTIONS[measureKey],
        max: durationMax,
        mean: durationMean,
        min: durationMin,
      };
    });

    this._intervalMarkersInUse.forEach((measureKey) => {
      const markName = this.getMeasureName(measureKey);
      const marks = performance.getEntriesByName(markName, 'mark');
      const startTimes = marks.map((entry) => entry.startTime);

      const deltas = [];
      for (let i = 1; i < startTimes.length; i++) {
        const delta = startTimes[i] - startTimes[i - 1];
        deltas.push(delta);
      }

      const deltaMean = average(deltas);
      const deltaMax = Math.max(...deltas);
      const deltaMin = Math.min(...deltas);

      tableData[measureKey] = {
        description: DESCRIPTIONS[measureKey],
        max: deltaMax,
        mean: deltaMean,
        min: deltaMin,
      };
    });

    logTable(tableData);
  }

  getMeasureName(measurementKey) {
    return `${this._sessionKey}-${measurementKey}`;
  }
  getMarkCommonPrefix(measurementKey, intervalIdentifier) {
    return `${this.getMeasureName(measurementKey)}-${intervalIdentifier}`;
  }

  getMarkStartName(measurementKey, intervalIdentifier) {
    return `${this.getMarkCommonPrefix(
      measurementKey,
      intervalIdentifier,
    )}-start`;
  }

  getMarkEndName(measurementKey, intervalIdentifier) {
    return `${this.getMarkCommonPrefix(
      measurementKey,
      intervalIdentifier,
    )}-end`;
  }
}

export const playbackPerformanceReview = new PerformanceReview(
  SESSION_KEYS.PLAYBACK,
);
export const textEditPerformanceReview = new PerformanceReview(
  SESSION_KEYS.TEXT_EDIT,
);
export const frameManagementPerformanceReview = new PerformanceReview(
  SESSION_KEYS.FRAME_MANAGEMENT,
);
